import { type ReactNode } from 'react';

import { RiBarChartLine } from '@remixicon/react';

import {
  ChartBlock,
  extractChartSpec,
  getChartTitle,
} from '@spotify/confidence-conversation-react';
import { FeatureFlagged } from '@spotify/confidence-flag-evaluation-react';
import {
  ElapsedTimer,
  type ToolGroupEntry,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';
import { useOptionalProjectContext } from '@spotify/plugin-confidence-projects-react';
import {
  PULSE_BLOCK_TYPE,
  SaveToPulseButton,
} from '@spotify/plugin-confidence-pulse-react';

import { confidenceChatPlugin } from '../plugin';

import classes from './CreateChartToolExtension.module.css';

const getSourceChatName = (eventName?: string): string | undefined => {
  const eventPathIndex = eventName?.indexOf('/events/') ?? -1;
  return eventPathIndex >= 0 ? eventName?.slice(0, eventPathIndex) : undefined;
};

const getCaptureDisplayName = (spec: string): string => {
  return getChartTitle(spec) ?? 'Chart';
};

export const CreateChartToolRenderer = ({
  entry,
  renderActions = ({ projectName, sourceChat, spec }) => (
    <SaveToPulseButton
      input={{
        projectName,
        sourceChat,
        sourceEvent: entry.eventName,
        suggestedName: getCaptureDisplayName(spec),
        blockType: PULSE_BLOCK_TYPE.CHART,
        visualizationSpec: spec,
      }}
     data-track-source-anchor="ea5b4d7378;create-chart-tool-renderer-save-to-pulse-button"/>
  ),
}: {
  entry: ToolGroupEntry;
  renderActions?:
    | ((chart: {
        projectName?: string;
        sourceChat: string;
        spec: string;
      }) => ReactNode)
    | null;
}) => {
  const projectName = useOptionalProjectContext()?.name;
  if (entry.result?.success === false) return null;

  const spec = extractChartSpec({
    content: entry.result?.content ?? '',
    metadata: { toolInput: entry.input },
  });
  if (!spec) return null;

  const sourceChat = getSourceChatName(entry.eventName);

  return (
    <div className={classes.chartWithActions}>
      {sourceChat && renderActions && (
        <FeatureFlagged with="pulses">
          <div className={classes.actions}>
            {renderActions({ projectName, sourceChat, spec })}
          </div>
        </FeatureFlagged>
      )}
      <ChartBlock content={spec} />
      {!entry.result && entry.startedAt !== undefined && (
        <ElapsedTimer startedAt={entry.startedAt} />
      )}
    </div>
  );
};

export const CreateChartToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'create-chart',
    predicate: tool => tool.endsWith('create_chart'),
    icon: RiBarChartLine,
    display: 'prominent',
    render: () => null,
    renderTool: entry => <CreateChartToolRenderer entry={entry} />,
  }),
);
